// src/Address.ts
import { ValidationResult, validateAddress } from "@taquito/utils";
import { CustomError } from "@umami/utils";
var parsePkh = (pkh) => {
  if (isValidContractPkh(pkh)) {
    return parseContractPkh(pkh);
  }
  if (isValidImplicitPkh(pkh)) {
    return parseImplicitPkh(pkh);
  }
  if (isValidSmartRollupPkh(pkh)) {
    return parseSmartRollupPkh(pkh);
  }
  throw new CustomError(`Cannot parse address type: ${pkh}`);
};
var isAddressValid = (pkh) => validateAddress(pkh) === ValidationResult.VALID;
var isValidContractPkh = (pkh) => isAddressValid(pkh) && !!pkh.match(/^KT1\w+/);
var isValidImplicitPkh = (pkh) => isAddressValid(pkh) && !!pkh.match(/^tz[1234]\w+/);
var isValidSmartRollupPkh = (pkh) => isAddressValid(pkh) && !!pkh.match(/^sr1\w+/);
var parseContractPkh = (pkh) => {
  if (isValidContractPkh(pkh)) {
    return { type: "contract", pkh };
  }
  throw new CustomError(`Invalid contract address: ${pkh}`);
};
var parseImplicitPkh = (pkh) => {
  if (isValidImplicitPkh(pkh)) {
    return { type: "implicit", pkh };
  }
  throw new CustomError(`Invalid implicit address: ${pkh}`);
};
var parseSmartRollupPkh = (pkh) => {
  if (isValidSmartRollupPkh(pkh)) {
    return { type: "smart_rollup", pkh };
  }
  throw new CustomError(`Invalid smart rollup address: ${pkh}`);
};

// src/constants.ts
var TEZ = "XTZ";
var TEZ_DECIMALS = 6;
var BLOCK_TIME = 10 * 1e3;
var verifiedTokens = [
  "KT1XnTn74bUtxHfDtBmm2bGZAQfhPbvKWR8o",
  "KT1PWx2mnDueood7fEmfbBDKx1D9BAnnXitn",
  "KT1XRPEPXbZK25r3Htzp2o1x7xdMMmfocKNW",
  "KT1XRPEPXbZK25r3Htzp2o1x7xdMMmfocKNW",
  "KT1XRPEPXbZK25r3Htzp2o1x7xdMMmfocKNW",
  "KT1K9gCRgaLRFKTErYt1wVxA3Frb9FjasjTV",
  "KT1Ha4yFVeyzw6KRAdkzq6TxDHB97KG4pZe8",
  "KT1JBNFcB5tiycHNdYGYCtR3kk6JaJysUCi8",
  "KT1Xobej4mc6XgEjDoJoHtTKgbD1ELMvcQuL",
  "KT1JVjgXPMMSaa6FkzeJcgb8q9cUaLmwaJUX",
  "KT193D4vozYnhGJQVtw7CoxxqphqUEEwK6Vb",
  "KT1LN4LPSqTMS7Sd2CJw4bbDGRkMv2t68Fy9",
  "KT1914CUZ7EegAFPbfgQMRkw8Uz5mYkEz2ui",
  "KT1UsSfaXyqcjSVPeiD7U1bWgKy3taYN7NWY",
  "KT19ovJhcsUn4YU8Q5L3BGovKSixfbWcecEA",
  "KT1TgmD7kXQzofpuc9VbTRMdZCS2e6JDuTtc",
  "KT1F1mn2jbqQCJcsNgYKVAQjvenecNMY2oPK",
  "KT1ErKVqEhG9jxXgUG2KGLW3bNM7zXHX8SDF",
  "KT1A5P4ejnLix13jtadsfV9GCnXLMNnab8UT",
  "KT1AM3PV1cwmGRw28DVTgsjjsjHvmL6z4rGh",
  "KT1UsSfaXyqcjSVPeiD7U1bWgKy3taYN7NWY",
  "KT1SjXiUX63QvdNMcM2m492f7kuf8JxXRLp4",
  "KT1MZg99PxMDEENwB4Fi64xkqAVh5d1rv8Z9",
  "KT1UsSfaXyqcjSVPeiD7U1bWgKy3taYN7NWY",
  "KT1XTxpQvo7oRCqp85LikEZgAZ22uDxhbWJv",
  "KT1VaEsVNiBoA56eToEK6n6BcPgh1tdx9eXi"
];

// src/derivationPathUtils.ts
var defaultDerivationPathTemplate = "44'/1729'/?'/0'";
var DEFAULT_DERIVATION_PATH_TEMPLATE = {
  label: "Default - m/44'/1729'/?'/0'",
  value: defaultDerivationPathTemplate
};
var AVAILABLE_DERIVATION_PATH_TEMPLATES = [
  DEFAULT_DERIVATION_PATH_TEMPLATE,
  { label: "m/44'/1729'/0'/?'", value: "44'/1729'/0'/?'" },
  { label: "m/44'/1729'/?'/0'/0'", value: "44'/1729'/?'/0'/0'" },
  { label: "m/44'/1729'/0'/0'/?'", value: "44'/1729'/0'/0'/?'" }
];
var getDefaultDerivationPath = (index) => makeDerivationPath(defaultDerivationPathTemplate, index);
var makeDerivationPath = (template, index, currentPath) => {
  if (!currentPath || currentPath === template) {
    return template.replace("?", index.toString());
  } else {
    return currentPath;
  }
};
var getDerivationPathNextIndex = (derivationPath, derivationPathTemplate) => {
  const currentPathParts = derivationPath.split("/");
  const templateParts = derivationPathTemplate.split("/");
  const index = templateParts.findLastIndex((part) => part.includes("?"));
  const currentValueNumber = parseInt(currentPathParts[index]);
  return currentValueNumber + 1;
};

// src/fakeSigner.ts
var FakeSigner = class {
  pk;
  pkh;
  constructor(pk, pkh) {
    this.pk = pk;
    this.pkh = pkh;
  }
  publicKey() {
    return Promise.resolve(this.pk);
  }
  publicKeyHash() {
    return Promise.resolve(this.pkh);
  }
  sign() {
    return Promise.reject(new Error("`sign` method not available"));
  }
  secretKey() {
    return Promise.reject(new Error("empty secret key"));
  }
};

// src/fetch.ts
import { RpcClient } from "@taquito/rpc";
var getProtocolSettings = (network) => new RpcClient(network.rpcUrl).getConstants();
var isAccountRevealed = async (pkh, network) => {
  const url = `${network.tzktApiUrl}/v1/accounts/${pkh}?select.fields=type,revealed`;
  const { type, revealed } = await fetch(url).then((res) => res.json());
  return type !== "empty" && revealed;
};

// src/format.ts
import { format } from "@taquito/utils";
import { BigNumber } from "bignumber.js";
var truncate = (name, len) => name.length > len ? name.slice(0, len - 3) + "..." : name;
var tezToMutez = (tez) => format("tz", "mutez", tez);
var mutezToTez = (mutez) => format("mutez", "tz", mutez);
var formatTezAmount = (mutez, minimumFractionDigits) => {
  const tezAmount = BigNumber(mutezToTez(mutez)).toNumber();
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: minimumFractionDigits ?? TEZ_DECIMALS,
    maximumFractionDigits: TEZ_DECIMALS
  });
  return formatter.format(tezAmount);
};
var prettyTezAmount = (mutez) => {
  const fee = formatTezAmount(mutez);
  return `${fee} ${TEZ}`;
};
var formatTezAmountMin0Decimals = (mutez) => {
  const fee = formatTezAmount(mutez, 0);
  return `${fee} ${TEZ}`;
};
var formatUsdAmount = (usd) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  });
  return formatter.format(BigNumber(usd).toNumber());
};
var formatPkh = (pkh) => `${pkh.slice(0, 5)}...${pkh.slice(-5)}`;

// src/helpers.ts
import crypto from "crypto";
import TransportWebUSB from "@ledgerhq/hw-transport-webusb";
import { DerivationType, LedgerSigner } from "@taquito/ledger-signer";
import { Parser } from "@taquito/michel-codec";
import { InMemorySigner } from "@taquito/signer";
import { TezosToolkit } from "@taquito/taquito";
import { CustomError as CustomError2 } from "@umami/utils";
var generateHash = () => {
  const utf8 = new TextEncoder().encode(Date.now().toString());
  const hash = crypto.createHash("sha256").update(utf8).digest("hex");
  return hash.slice(0, 8);
};
var curveToDerivationType = (curve) => {
  switch (curve) {
    case "ed25519":
      return DerivationType.ED25519;
    case "secp256k1":
      return DerivationType.SECP256K1;
    case "p256":
      return DerivationType.P256;
    case "bip25519":
      throw new CustomError2("bip25519 is not supported in Tezos");
  }
};
var makeSigner = async (config) => {
  switch (config.type) {
    case "social":
    case "mnemonic":
    case "secret_key":
      return new InMemorySigner(config.secretKey);
    case "ledger": {
      const devices = await TransportWebUSB.list();
      for (let i = 0; i < devices.length; i++) {
        devices[i].close();
      }
      const transport = await TransportWebUSB.create();
      const signer = new LedgerSigner(
        transport,
        config.account.derivationPath,
        false,
        // PK Verification not needed
        curveToDerivationType(config.account.curve)
      );
      return signer;
    }
    case "fake":
      return new FakeSigner(config.signer.pk, config.signer.address.pkh);
  }
};
var makeToolkit = async (config) => {
  const toolkit = new TezosToolkit(config.network.rpcUrl);
  const signer = await makeSigner(config);
  toolkit.setSignerProvider(signer);
  return toolkit;
};
var getPublicKeyPairFromSk = async (sk) => {
  const signer = new InMemorySigner(sk);
  return { pk: await signer.publicKey(), pkh: await signer.publicKeyHash() };
};
var derivePublicKeyPair = async (mnemonic, derivationPath, curve) => deriveSecretKey(mnemonic, derivationPath, curve).then(getPublicKeyPairFromSk);
var deriveSecretKey = (mnemonic, derivationPath, curve) => InMemorySigner.fromMnemonic({
  mnemonic,
  derivationPath,
  curve
}).secretKey();
var isValidMichelson = (object) => {
  try {
    new Parser().parseJSON(object);
    return true;
  } catch {
    return false;
  }
};
var getLedgerPublicKeyPair = async (derivationPath, curve) => {
  const transport = await TransportWebUSB.create();
  const ledgerSigner = new LedgerSigner(
    transport,
    derivationPath,
    true,
    curveToDerivationType(curve)
  );
  const pk = await ledgerSigner.publicKey();
  const pkh = await ledgerSigner.publicKeyHash();
  await transport.close();
  return { pk, pkh };
};
var getIPFSurl = (ipfsPath) => ipfsPath?.replace("ipfs://", "https://ipfs.io/ipfs/");
var decryptSecretKey = async (secretKey, password) => {
  try {
    const signer = await InMemorySigner.fromSecretKey(secretKey.trim(), password);
    return await signer.secretKey();
  } catch (error) {
    const message = error.message || "";
    if (message.includes("can't decrypt secret key")) {
      throw new CustomError2("Key-password pair is invalid");
    }
    if (message.includes("Invalid private key")) {
      throw new CustomError2("Invalid secret key: checksum doesn't match");
    }
    throw new CustomError2(error.message);
  }
};

// src/Network.ts
var MAINNET = {
  name: "mainnet",
  rpcUrl: process.env.TEZOS_MAINNET_RPC_URL ?? "https://tezos-mainnet.octez.io",
  tzktApiUrl: process.env.TEZOS_MAINNET_TZKT_API_URL ?? "https://api.mainnet.tzkt.io",
  explorerUrl: "https://tzkt.io",
  buyTezUrl: "https://widget.wert.io",
  namespace: "tezos",
  identifier: "mainnet",
  symbol: "XTZ",
  chain: "tezos"
};
var SHADOWNET = {
  name: "shadownet",
  rpcUrl: process.env.TEZOS_SHADOWNET_RPC_URL ?? "https://rpc.shadownet.teztnets.com",
  tzktApiUrl: process.env.TEZOS_SHADOWNET_TZKT_API_URL ?? "https://api.shadownet.tzkt.io",
  explorerUrl: "https://shadownet.tzkt.io",
  buyTezUrl: "https://faucet.shadownet.teztnets.com",
  namespace: "tezos",
  identifier: "shadownet",
  symbol: "XTZ",
  chain: "tezos"
};
var MICHELSON = {
  name: "michelson",
  rpcUrl: process.env.TEZOS_MICHELSON_RPC_URL ?? "https://michelson.etherlink.mainnet.octez.io",
  tzktApiUrl: process.env.TEZOS_MICHELSON_TZKT_API_URL ?? "https://api.etherlink.tzkt.io",
  explorerUrl: "https://etherlink.tzkt.io",
  buyTezUrl: "",
  namespace: "tezos",
  identifier: "michelson",
  symbol: "XTZ",
  chain: "tezos"
};
var MICHELSON_SHADOWNET = {
  name: "michelson shadownet",
  rpcUrl: process.env.TEZOS_MICHELSON_SHADOWNET_RPC_URL ?? "https://michelson.etherlink.shadownet.octez.io",
  tzktApiUrl: process.env.TEZOS_MICHELSON_SHADOWNET_TZKT_API_URL ?? "https://api.shadownet.etherlink.tzkt.io",
  explorerUrl: "https://shadownet.etherlink.tzkt.io",
  buyTezUrl: "https://faucet.shadownet.teztnets.com",
  namespace: "tezos",
  identifier: "michelson-shadownet",
  symbol: "XTZ",
  chain: "tezos"
};
var PREVIEWNET = {
  name: "previewnet",
  rpcUrl: process.env.TEZOS_PREVIEWNET_RPC_URL ?? "https://michelson.previewnet.tezosx.nomadic-labs.com",
  tzktApiUrl: process.env.TEZOS_PREVIEWNET_TZKT_API_URL ?? "https://api.previewnet.tezosx.tzkt.io",
  explorerUrl: "https://previewnet.tezosx.tzkt.io",
  buyTezUrl: "https://faucet.previewnet.tezosx.nomadic-labs.com",
  namespace: "tezos",
  identifier: "previewnet",
  symbol: "XTZ",
  chain: "tezos"
};
var isDefault = (network) => !!DefaultNetworks.find((n) => n.name === network.name);
var DefaultNetworks = [
  MAINNET,
  SHADOWNET,
  MICHELSON,
  MICHELSON_SHADOWNET,
  PREVIEWNET
];

// src/testUtils.ts
var validSmartRollupAddresses = ["sr1Ghq66tYK9y3r8CC1Tf8i8m5nxh8nTvZEf"];
var mockSmartRollupAddress = (index) => ({
  type: "smart_rollup",
  pkh: validSmartRollupAddresses[index]
});
var validContractAddresses = [
  "KT1QuofAgnsWffHzLA7D78rxytJruGHDe7XG",
  "KT1CSKPf2jeLpMmrgKquN2bCjBTkAcAdRVDy",
  "KT1EctCuorV2NfVb1XTQgvzJ88MQtWP8cMMv",
  "KT1Ex8LrDbCrZuTgmWin8eEo7HFw74jAqTvz"
];
var mockContractAddress = (index) => ({
  type: "contract",
  pkh: validContractAddresses[index]
});
var validMockPkhs = [
  "tz1gUNyn3hmnEWqkusWPzxRaon1cs7ndWh7h",
  "tz1UZFB9kGauB6F5c2gfJo4hVcvrD8MeJ3Vf",
  "tz1ikfEcj3LmsmxpcC1RMZNzBHbEmybCc43D",
  "tz1g7Vk9dxDALJUp4w1UTnC41ssvRa7Q4XyS",
  "tz1i9imTXjMAW5HP5g3wq55Pcr43tDz8c3VZ",
  "tz1PB7jN9GnNppn8hEbrherUAt4n3bv3wgNn",
  "tz1cuj9Cgi19KMRi83UuypS89kXM435qdhmy",
  "tz1Kt4P8BCaP93AEV4eA7gmpRryWt5hznjCP",
  "tz1cX93Q3KsiTADpCC4f12TBvAmS5tw7CW19",
  "tz1NEKxGEHsFufk87CVZcrqWu8o22qh46GK6",
  "tz1W2hEsS1mj7dHPZ6267eeM4HDWJoG3s13n"
];
var mockImplicitAddress = (index) => {
  if (index >= validMockPkhs.length) {
    throw Error("index out of bound");
  }
  return { type: "implicit", pkh: validMockPkhs[index] };
};
var mockPk = (index) => `edpkuwYWCugiYG7nMnVUdopFmyc3sbMSiLqsJHTQgGtVhtSdLSw6H${index}`;
export {
  AVAILABLE_DERIVATION_PATH_TEMPLATES,
  BLOCK_TIME,
  DEFAULT_DERIVATION_PATH_TEMPLATE,
  DefaultNetworks,
  FakeSigner,
  MAINNET,
  MICHELSON,
  MICHELSON_SHADOWNET,
  PREVIEWNET,
  SHADOWNET,
  TEZ,
  TEZ_DECIMALS,
  curveToDerivationType,
  decryptSecretKey,
  defaultDerivationPathTemplate,
  derivePublicKeyPair,
  deriveSecretKey,
  formatPkh,
  formatTezAmount,
  formatTezAmountMin0Decimals,
  formatUsdAmount,
  generateHash,
  getDefaultDerivationPath,
  getDerivationPathNextIndex,
  getIPFSurl,
  getLedgerPublicKeyPair,
  getProtocolSettings,
  getPublicKeyPairFromSk,
  isAccountRevealed,
  isAddressValid,
  isDefault,
  isValidContractPkh,
  isValidImplicitPkh,
  isValidMichelson,
  isValidSmartRollupPkh,
  makeDerivationPath,
  makeSigner,
  makeToolkit,
  mockContractAddress,
  mockImplicitAddress,
  mockPk,
  mockSmartRollupAddress,
  mutezToTez,
  parseContractPkh,
  parseImplicitPkh,
  parsePkh,
  parseSmartRollupPkh,
  prettyTezAmount,
  tezToMutez,
  truncate,
  verifiedTokens
};
